import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { useUser } from '../context/UserContext';
import logger from '../utils/logger';

/**
 * Component to handle team invitations
 * This component checks for invitation codes in the URL and verifies them
 * It can be used during signup or after login to join a team
 */
const InvitationHandler = ({ onInvitationVerified, showUI = true, onlyVerify = false }) => {
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract invitation code from URL query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invitationCode = queryParams.get('invitation');
    
    if (invitationCode) {
      verifyInvitation(invitationCode);
    }
  }, [location.search]);

  // Verify the invitation code with the backend
  const verifyInvitation = async (code) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_URL}/api/invitations/verify/${code}`, {
        timeout: 15000 // 15 second timeout
      });
      
      if (response.data && response.data.success) {
        // Add detailed logging to inspect the invitation data structure
        logger.debug('InvitationHandler: Invitation data received:', response.data.invitation);
        
        // Extract role and sport from the response, ensuring they are strings
        let roleValue = response.data.invitation.role;
        let sportValue = response.data.invitation.team?.sport;
        let nameValue = response.data.invitation.name;
        let emailValue = response.data.invitation.email;
        
        // Check if role is an object and extract a string value if possible
        if (roleValue && typeof roleValue === 'object') {
          logger.debug('InvitationHandler: Role is an object:', roleValue);
          // Try to extract a string value from common properties
          roleValue = roleValue.name || roleValue.type || roleValue.value || roleValue.id || 'player';
          logger.debug('InvitationHandler: Extracted role string:', roleValue);
        }
        
        // Check if sport is an object and extract a string value if possible
        if (sportValue && typeof sportValue === 'object') {
          logger.debug('InvitationHandler: Sport is an object:', sportValue);
          // Try to extract a string value from common properties
          sportValue = sportValue.name || sportValue.type || sportValue.value || sportValue.id || '';
          logger.debug('InvitationHandler: Extracted sport string:', sportValue);
        }
        
        // If user is not logged in, redirect to new-profile with invitation data WITHOUT setting invitation state
        if (!user) {
          logger.info('InvitationHandler: User not logged in, redirecting to new-profile with invitation data');
          logger.debug('InvitationHandler: Invitation data for registration:', {
            invitationCode: code,
            initialName: nameValue,
            initialEmail: emailValue,
            initialRole: roleValue,
            initialSport: sportValue
          });
          
          // Navigate to new-profile instead of register
          // This is the correct component for handling invitations during registration
          navigate('/new-profile', { 
            state: { 
              invitationCode: code,
              initialName: nameValue,
              initialEmail: emailValue,
              initialRole: roleValue,
              initialSport: sportValue
            } 
          });
          
          return;
        }
        
        // Only set invitation state if we're NOT redirecting
        // Ensure the local invitation state includes the code, role, and sport as strings
        const verifiedInvitation = {
          ...response.data.invitation,
          code,
          name: nameValue,
          email: emailValue,
          role: roleValue,
          sport: sportValue
        };
        setInvitation(verifiedInvitation);
        
        // Call the callback if provided, ensuring the code is part of the passed object
        if (onInvitationVerified) {
          logger.info('InvitationHandler: About to call onInvitationVerified with:', verifiedInvitation);
          onInvitationVerified(verifiedInvitation);
        }
      } else {
        setError('Invalid invitation code.');
      }
    } catch (err) {
      logger.error('Error verifying invitation', err);
      
      // Provide more specific error messages based on error type
      if (err.code === 'ECONNABORTED') {
        setError('Server request timed out. Please try again later.');
      } else if (err.response?.status === 404) {
        setError('Invitation not found. It may have expired or been cancelled.');
      } else if (err.response?.status === 401) {
        setError('You are not authorized to view this invitation.');
      } else {
        setError(err.response?.data?.message || 'Failed to verify invitation. It may be expired or invalid.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Join team with the invitation code
  const joinTeam = async () => {
    if (!invitation || !invitation.code || !user) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('You must be logged in to join a team.');
        setLoading(false);
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/api/teams/join/${invitation.code}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000 // 15 second timeout
        }
      );
      
      if (response.data && response.data.success) {
        // Redirect based on user role
        if (user.role?.toLowerCase() === 'coach') {
          navigate('/coach-dashboard');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      logger.error('Error joining team', err);
      
      // Provide more specific error messages based on error type
      if (err.code === 'ECONNABORTED') {
        setError('Server request timed out. Please try again later.');
      } else if (err.response?.status === 404) {
        setError('Invitation not found. It may have expired or been cancelled.');
      } else if (err.response?.status === 401) {
        setError('You are not authorized to join this team.');
      } else if (err.response?.status === 409) {
        setError('You are already a member of this team.');
      } else {
        setError(err.response?.data?.message || 'Failed to join team. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // If not showing UI, just return null
  if (!showUI) return null;
  
  // If no invitation found, don't render anything
  if (!invitation) {
    return (
      <div className="mt-4">
        {loading && <div className="alert alert-info">Verifying invitation...</div>}
        {error && <div className="alert alert-error">{error}</div>}
      </div>
    );
  }

  // Render invitation details and join button
  return (
    <div className="mt-4">
      {loading ? (
        <div className="alert alert-info">
          <div className="flex items-center">
            <span className="loading loading-spinner loading-sm mr-2"></span>
            Processing...
          </div>
        </div>
      ) : (
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title">Team Invitation</h2>
            <p>
              You've been invited to join <strong>{invitation.team?.name}</strong> as a{' '}
              <strong>{invitation.role}</strong>.
            </p>
            
            {user && !onlyVerify ? (
              <div className="mt-4">
                <button 
                  className="btn btn-primary" 
                  onClick={joinTeam}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-xs mr-2"></span>
                      Joining...
                    </>
                  ) : (
                    <>Join Team</>
                  )}
                </button>
              </div>
            ) : onlyVerify ? (
              <p className="mt-2 text-sm">
                You'll be able to join this team after login.
              </p>
            ) : (
              <p className="mt-2 text-sm">
                Complete your registration to join this team.
              </p>
            )}
            
            {error && <div className="alert alert-error mt-4">{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationHandler;
